import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { requestPasswordReset } from "@/lib/supabase/auth";
import { debug } from "@/lib/utils";
import TradesPurpleLayout from "../components/Layout";
import { Helmet } from "react-helmet-async";

const schema = z.object({
  email: z.string().email("Please enter a valid email"),
});
type FormValues = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    try {
      await requestPasswordReset(data.email);
      setSubmitted(true);
    } catch (error) {
      debug.error("Reset request error:", error);
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Forgot password</title>
      </Helmet>

      <div className="flex items-center justify-center">
        <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>
              Reset your password
            </CardTitle>
            <CardDescription className="text-center text-purple-700">
              {submitted
                ? "If we have an account for that email, you'll get a reset link in a moment."
                : "Enter your email and we'll send you a reset link."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!submitted && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel style={{ color: "#5D4A9C" }}>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            className="border-2 border-purple-300 focus:border-purple-500"
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage className="text-red-600" />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full bg-purple-700 hover:bg-purple-800"
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-sm text-center text-purple-700">
              <Link to="/auth/login" style={{ color: "#5D4A9C" }} className="font-semibold hover:underline">
                Back to login
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </TradesPurpleLayout>
  );
}
